import {Injectable} from '@angular/core';

/**
 * Session-scoped key/value store backed by the browser's sessionStorage.
 *
 * In sandboxed / privacy browser contexts (e.g. a third-party iframe with
 * cookies blocked, or locked-down mobile browsers) reading or writing
 * sessionStorage throws a SecurityError — even when an initial support probe
 * succeeds. Auth/session plumbing (token, socket id, authenticated flag) reads
 * through this service on every request, so silently dropping reads/writes
 * leaves the app quietly broken. To degrade gracefully we fall back to an
 * in-memory Map.
 *
 * The fallback lives for the page lifecycle only — unlike real sessionStorage it
 * does NOT survive a reload or an OAuth redirect — but that is strictly better
 * than dropping every read and write on the floor.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  private sessionStorageSupported = false;
  private readonly memoryFallback = new Map<string, string>();

  constructor() {
    try {
      // Reading the property itself can throw a SecurityError in sandboxed /
      // privacy contexts, so guard it.
      this.sessionStorageSupported = window.sessionStorage != null;
    } catch {
      this.sessionStorageSupported = false;
    }
  }

  public setItem(key: string, value: any) {
    const serialized = JSON.stringify(value);
    if (this.sessionStorageSupported) {
      try {
        sessionStorage.setItem(key, serialized);
        return;
      } catch {
        // Access denied at runtime even though the support probe passed — latch
        // off so every later read/write uses the in-memory store consistently.
        // Otherwise a later successful read could serve a stale real value over
        // this newer one (and a failed delete could resurrect a stale value).
        this.sessionStorageSupported = false;
      }
    }
    this.memoryFallback.set(key, serialized);
  }

  public getItem(key: string, defaultValue?: any): any {
    const raw = this.readRaw(key);
    if (raw !== null) {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }
    return defaultValue ?? null;
  }

  public updateItem(key: string, value: any) {
    this.setItem(key, value);
  }

  public deleteItem(key: string) {
    this.memoryFallback.delete(key);
    if (this.sessionStorageSupported) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Access denied at runtime — latch off; the in-memory copy is cleared.
        this.sessionStorageSupported = false;
      }
    }
  }

  private readRaw(key: string): string | null {
    if (this.sessionStorageSupported) {
      try {
        const item = sessionStorage.getItem(key);
        if (item !== null) {
          return item;
        }
      } catch {
        // Access denied at runtime — latch off (see setItem) and fall through.
        this.sessionStorageSupported = false;
      }
    }
    return this.memoryFallback.has(key) ? this.memoryFallback.get(key)! : null;
  }

}
